import React, { useEffect, useRef } from "react";
import { Bluetooth, BluetoothOff, Sailboat } from "lucide-react";
import PowerChart from "../PowerChart";
import DashboardTimingHeader from "./DashboardTimingHeader";
import HrTile from "./HrTile";
import {
  PrimaryStat,
  rowerPrimaryStats,
  DashboardTransport,
  ExtraStats,
  PauseButton,
  rowerExtraStats,
} from "./dashboardWidgets";
import { cn } from "../../../lib/utils";

const formatSpm = (spm) => (spm == null ? "—" : String(Math.round(spm)));
const formatPace = (sec) => {
  if (sec == null) return "—";
  const m = Math.floor(sec / 60);
  const s = String(Math.round(sec % 60)).padStart(2, "0");
  return `${m}:${s}`;
};
const formatDistance = (m) => (m == null ? "—" : `${m}`);

/**
 * Rower dashboard — same ref-painting trick as BikeDashboard so 10 Hz packets
 * don't trigger renders. Primary metric: Power (W). Secondary tiles: Stroke
 * Rate (spm) and Pace (m:ss /500m). Distance is shown as a header counter.
 */
const RowerDashboard = ({
  subscribe,
  metricsRef,
  summaryRef,
  status,
  deviceName,
  onAddDevice,
  timing,
  hr,
  hrBpm,
  isPaused,
  isRunning,
  onPlayPause,
  pauseMenu,
}) => {
  const spmNodeRef = useRef(null);
  const paceNodeRef = useRef(null);
  const distanceNodeRef = useRef(null);
  const avgNodeRef = useRef(null);
  const maxNodeRef = useRef(null);
  const rootRef = useRef(null);
  const visibleRef = useRef(true);

  const paint = () => {
    if (!visibleRef.current) return;
    const m = metricsRef?.current;
    if (m && spmNodeRef.current) {
      spmNodeRef.current.textContent = formatSpm(m.stroke_rate_spm);
    }
    if (m && paceNodeRef.current) {
      paceNodeRef.current.textContent = formatPace(m.pace_sec_per_500m);
    }
    if (m && distanceNodeRef.current) {
      distanceNodeRef.current.textContent = formatDistance(m.total_distance_m);
    }
    const s = summaryRef?.current;
    if (avgNodeRef.current) {
      avgNodeRef.current.textContent =
        s?.avg_power_watts != null ? `${s.avg_power_watts} W` : "— W";
    }
    if (maxNodeRef.current) {
      maxNodeRef.current.textContent =
        s?.max_power_watts != null ? `${s.max_power_watts} W` : "— W";
    }
  };

  useEffect(() => {
    paint();
    const unsubscribe = subscribe?.(() => paint());
    return () => unsubscribe?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscribe]);

  useEffect(() => {
    const node = rootRef.current;
    if (!node) return undefined;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          visibleRef.current = entry.isIntersecting;
          if (visibleRef.current) paint();
        }
      },
      { threshold: 0.25 },
    );
    obs.observe(node);
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isConnected = status === "connected";
  const showHr = !!hr || hrBpm != null;

  return (
    <div
      ref={rootRef}
      className="relative flex flex-col items-stretch h-full max-w-lg mx-auto p-4 pb-16 gap-4 text-white overflow-y-auto"
    >
      <DashboardTimingHeader
        {...timing}
        action={
          onPlayPause && !isPaused ? (
            <PauseButton isRunning={isRunning} onPlayPause={onPlayPause} />
          ) : null
        }
      />

      <header className="flex items-center justify-between text-sm text-white/60">
        <div className="flex items-center gap-1.5">
          <Sailboat className="w-4 h-4" />
          <span className="tracking-wide uppercase">Rower</span>
          <span className="ml-2 tabular-nums">
            <span ref={distanceNodeRef}>—</span>
            <span className="text-white/40"> m</span>
          </span>
        </div>
        <div className="flex items-center gap-1.5 truncate max-w-[60%]">
          {isConnected ? (
            <>
              <Bluetooth className="w-3.5 h-3.5 text-blue-400" />
              <span className="truncate">{deviceName || "Rower"}</span>
            </>
          ) : (
            <>
              <BluetoothOff className="w-3.5 h-3.5" />
              <span>
                {status === "reconnecting" ? "Reconnecting…" : "Not connected"}
              </span>
            </>
          )}
        </div>
      </header>

      <PrimaryStat
        stats={rowerPrimaryStats}
        storageKey="ergPrimaryStat.rower"
        subscribe={subscribe}
        metricsRef={metricsRef}
        hr={hr}
        hrBpm={hrBpm}
        timing={timing}
        visibleRef={visibleRef}
      />

      <div className="flex flex-col gap-1">
        <div className="text-xs uppercase tracking-widest text-white/40">
          {showHr ? "Power · HR · last 60 s" : "Power · last 60 s"}
        </div>
        <div className="h-32 w-full rounded-lg bg-white/[0.03] border border-white/5 overflow-hidden">
          <PowerChart
            subscribe={subscribe}
            hr={hr}
            hrBpm={hrBpm}
            className="w-full h-full block"
          />
        </div>
      </div>

      <div
        className={cn(
          "grid gap-3",
          showHr ? "grid-cols-3" : "grid-cols-2",
        )}
      >
        <div className="rounded-lg bg-white/[0.03] border border-white/5 p-3 flex flex-col items-center">
          <span
            ref={spmNodeRef}
            className="text-4xl font-mono font-bold tabular-nums"
          >
            —
          </span>
          <span className="text-xs uppercase tracking-widest text-white/40 mt-1">
            Stroke rate · spm
          </span>
        </div>
        <div className="rounded-lg bg-white/[0.03] border border-white/5 p-3 flex flex-col items-center">
          <span
            ref={paceNodeRef}
            className="text-4xl font-mono font-bold tabular-nums"
          >
            —
          </span>
          <span className="text-xs uppercase tracking-widest text-white/40 mt-1">
            Pace · /500m
          </span>
        </div>
        {showHr && <HrTile hr={hr} bpm={hrBpm} />}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="flex items-baseline justify-between rounded-lg bg-white/[0.03] border border-white/5 px-3 py-2">
          <span className="text-xs uppercase tracking-widest text-white/40">Avg</span>
          <span
            ref={avgNodeRef}
            className="text-lg font-mono font-bold tabular-nums"
          >
            — W
          </span>
        </div>
        <div className="flex items-baseline justify-between rounded-lg bg-white/[0.03] border border-white/5 px-3 py-2">
          <span className="text-xs uppercase tracking-widest text-white/40">Max</span>
          <span
            ref={maxNodeRef}
            className="text-lg font-mono font-bold tabular-nums"
          >
            — W
          </span>
        </div>
      </div>

      <ExtraStats
        stats={rowerExtraStats}
        subscribe={subscribe}
        metricsRef={metricsRef}
        visibleRef={visibleRef}
      />

      {!isConnected && onAddDevice && (
        <div className="mt-auto flex justify-center pb-8">
          <button
            type="button"
            onClick={onAddDevice}
            className="flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-sm text-white/80 hover:text-white hover:border-white/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Sailboat className="w-4 h-4" />
            Connect equipment
          </button>
        </div>
      )}

      <DashboardTransport
        isPaused={isPaused}
        isRunning={isRunning}
        onPlayPause={onPlayPause}
        timing={timing}
        pauseMenu={pauseMenu}
      />
    </div>
  );
};

export default RowerDashboard;
